import Table, { IColumnTable } from '@components/table-devextreme/Table';
import { RootState } from 'app/store';
import Helpers from 'helpers/Helpers';
import Layout from '@components/Layout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { EModePanelSelect, ICallBackEvents, SelectPanel } from '../../../components/SelectPanelReport/SelectPanel';
import TableTitle from '@components/table-devextreme/TableTitle';

import { useBaoCaoDoanhThuTheoKinhDoanh } from 'apis/queries';
import { updatePageLoadingApi, updateResetDateTime } from '@slice/select.slice';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { message, Tabs, Button, Empty } from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { convertTime } from '@pages/OrganizedSales/CreateOrders/utilOrder';
import { MINPAGESIZE3 } from 'global/Global';
import AdminLogApi from 'apis/api/AdminLog';
import CustomerDebtDetailModal from './CustomerDebtDetailModal';

import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ViewButton from '@components/table-devextreme/buttons/ViewButton';
import { getAllCategoryPros } from '@slice/production.slice';
import moment from 'moment';

// Hàm hỗ trợ chuẩn hoá chuỗi: xoá khoảng trắng 2 đầu và đưa về chữ thường
const normalizeToCompare = (str: string) => (str ? str.trim().toLowerCase() : '');

const EXCLUDED_PRODUCTS = new Set(
  [
    'Bộ Camera AD',
    'Bộ Camera BM',
    'Mắt cam',
    'Sim 4G',
    'Sim gói cước Mobi',
    'Sim gói cước Mobi +',
    'Sim gói cước Viettel',
    'Sim gói cước Viettel +',
    'Bộ Taxi',
    'Taxi',
    'Anten',
    'Bộ màn hình HDMI',
    'Dây nguồn',
    'Định vị 2G',
    'Khác',
    'LGĐL',
    'Phần mềm',
    'Phụ kiện',
    'Sim 4G cũ',
    'Hàng xuất VP',
    'Andon',
    'Bus',
    'Linh kiện'
  ].map(normalizeToCompare)
);
// Lưu vào Set lúc này toàn bộ là chữ thường (vd: 'bộ camera ad', 'sim 4g')

// ----------------------------------------------------------------------------------
// Kiểu dữ liệu
// ----------------------------------------------------------------------------------
interface IHangHoaKinhDoanh {
  maSanPham: string;
  tenSanPham: string;
  soLuong: number;
  tongTien: number;
  categoryId: number;
}

interface INhomKhachHangKinhDoanh {
  cooperationTypeId: number;
  maKhachHang: string;
  tenKhachHang: string;
  doanhSoBan: number;
  no: number;
  noDauKy: number;
  tienMat?: number;
  chuyenKhoan?: number;
  thuNo?: number;
  noHangHoa?: number;
  tongNo?: number;
  tienDaThu?: number;
  // --- Bổ sung ---
  tenLienHe?: string;
  sdtLienHe?: string;
  loaiDon?: string | string[]; // có thể là 1 loại hoặc nhiều loại đơn phát sinh trong kỳ
  ngayThanhToan?: string | null;
}

interface IKinhDoanhReport {
  saleAccount: string;
  doanhSoBan: number;
  tienDaThu: number;
  tienMat: number;
  chuyenKhoan: number;
  thuNo: number;
  hangHoa: IHangHoaKinhDoanh[];
  noHangHoa: number;
  tongNo: number;
  no?: number;
  noHangHoaDauKy: number;
  tongNoDauKy: number;
  nhomKhachHang: INhomKhachHangKinhDoanh[];
}

const coopType = (cooperationTypeId?: number) => {
  switch (cooperationTypeId) {
    case 1:
      return 'Khách lẻ';
    case 2:
      return 'Công ty';
    case 3:
      return 'Đại lý';
    case 4:
      return 'HTX';
    case 5:
      return 'Showroom';
    case 6:
      return 'Cộng tác viên';
    default:
      return 'Khác';
  }
};

const hideZero = (value?: number): number | null => (value ? value : null);

const sortAndIndex = (arr: any[]) =>
  arr
    .sort((a, b) =>
      (a.saleAccount || '').localeCompare(b.saleAccount || '', 'vi', { sensitivity: 'variant', caseFirst: 'upper' })
    )
    .map((value, index) => ({ ...value, Id: index + 1 }));

// Ký tự an toàn để dùng làm dataField (DevExtreme không thích dấu '.' trong dataField)
const toSafeKey = (code: string) => `hh_${code}`.replace(/\s+/g, '_').replace(/\./g, '_');

const normalizeArrayText = (value?: any): string => {
  if (!value) return '';
  if (Array.isArray(value)) return Array.from(new Set(value.filter(Boolean))).join(', ');
  return String(value);
};

export default function ReportDoanhThuKD({ tabKeyActive }: any) {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const formatNgayThanhToan = (value?: string | null) => (value ? moment.utc(value).format('DD/MM/YYYY HH:mm:ss') : '');
  // --- States ---
  // 4 bảng tương ứng cấu trúc file mẫu: Tổng hợp NV / Chi tiết Khách hàng / Hàng hóa (Doanh số - pivot) / Hàng hóa (Số lượng - pivot)
  const [dataTongHop, setDataTongHop] = useState<any[]>([]);
  const [dataKH, setDataKH] = useState<any[]>([]);
  // const [activeTab, setActiveTab] = useState<'tonghop' | 'kh' | 'hhds' | 'hhsl'>('tonghop');
  const [activeTab, setActiveTab] = useState<'tonghop' | 'kh' | 'hh'>('tonghop');
  // State lưu dữ liệu raw để xuất Excel
  const [reportData, setReportData] = useState<IKinhDoanhReport[]>([]);
  const timeSelected = useSelector((state: RootState) => state.select.selectedTime);
  const [timeDebtOrders, setTimeDebtOrders] = useState<any>('');
  const [debtModalVisible, setDebtModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<{ maKhachHang: string; saleAccount: string } | null>(null);
  const category = useSelector((state: RootState) => state.production.productCateData);
  const selectedArrayListSalesV3 = useSelector((state: RootState) => state.select.selectedArrayListSalesV3);

  useEffect(() => {
    dispatch(getAllCategoryPros());
  }, []);
  // Selector
  const {
    selectedBranch,
    orderCoopType,
    orderTime,
    listSales,
    paymentMethod,
    typeBranch: typeBranch1,
    selectedArrayOrder
  } = useSelector((state: RootState) => state.select);

  const [query1, setQuery1] = React.useState<{ [key: string]: any }>({
    maDonHang: '',
    orderTypeId: [0],
    from: convertTime.begin,
    to: convertTime.end,
    orderStatus: 'Complete',
    companyBranchId: 0,
    maKhachHang: '',
    loaiHinhHopTac: 0,
    staffExecute: '',
    staffCreate: '',
    companyName: '',
    hinhThucThanhToan: '',
    typeDate: 1,
    typeBranch: 1,
    saleAccount: ''
  });

  const openCustomerDebtDetail = (row: any) => {
    if (!row.maChiTiet || row.maChiTiet === '–') return;
    setSelectedCustomer({ maKhachHang: row.maChiTiet, saleAccount: row.saleAccount });
    setDebtModalVisible(true);
  };

  // Dựng 2 bộ dữ liệu dạng dòng: Tổng hợp NV / Chi tiết Khách hàng
  // (bảng Hàng hóa được dựng riêng ở phần pivot bên dưới, dựa trên reportData)
  const onLoadDetailAllOrder = (data: IKinhDoanhReport[]) => {
    const tongHopRows: any[] = [];
    const khRows: any[] = [];

    (data || []).forEach(kd => {
      const thuTien = kd.tienDaThu || 0; // (kd.tienMat || 0) + (kd.chuyenKhoan || 0) + (kd.thuNo || 0);
      const noKhac = (kd.tongNo || kd.no || 0) - (kd.noHangHoa || 0);

      tongHopRows.push({
        saleAccount: kd.saleAccount,
        doanhSoBan: kd.doanhSoBan,
        tienMat: kd.tienMat,
        chuyenKhoan: kd.chuyenKhoan,
        thuNo: kd.thuNo,
        noHangHoa: kd.noHangHoa,
        tongNo: kd.tongNo,
        thuTien: thuTien,
        noKhac: noKhac
      });

      (kd.nhomKhachHang || []).forEach(kh => {
        const noHangHoaKH = kh.noHangHoa ?? 0;
        const tongNoKH = kh.tongNo ?? kh.no ?? 0;
        const noKhacKH = tongNoKH - noHangHoaKH;
        const tienMatKH = kh.tienMat || 0;
        const chuyenKhoanKH = kh.chuyenKhoan || 0;
        const thuNoKH = kh.thuNo || 0;
        const thuTienKH = kh.tienDaThu || 0;

        khRows.push({
          saleAccount: kd.saleAccount,
          maChiTiet: kh.maKhachHang,
          tenChiTiet: kh.tenKhachHang,
          cooperationTypeId: kh.cooperationTypeId,
          countKH: 1,
          // tenLienHe: kh.tenLienHe || '',
          // sdtLienHe: kh.sdtLienHe || '',
          // loaiDon: normalizeArrayText(kh.loaiDon),
          doanhSoBanKH: hideZero(kh.doanhSoBan),
          doanhSoBanKHSum: kh.doanhSoBan,
          tienMatKH: hideZero(tienMatKH),
          tienMatKHSum: tienMatKH,
          chuyenKhoanKH: hideZero(chuyenKhoanKH),
          chuyenKhoanKHSum: chuyenKhoanKH,
          thuNoKH: hideZero(thuNoKH),
          thuNoKHSum: thuNoKH,
          thuTienKH: hideZero(thuTienKH),
          thuTienKHSum: thuTienKH,
          noHangHoaKH: hideZero(noHangHoaKH),
          noHangHoaKHSum: noHangHoaKH,
          noKhacKH: hideZero(noKhacKH),
          noKhacKHSum: noKhacKH,
          tongNoKH: hideZero(tongNoKH),
          tongNoKHSum: tongNoKH
        });
      });

      if ((kd.nhomKhachHang || []).length === 0) {
        khRows.push({
          saleAccount: kd.saleAccount,
          maChiTiet: '–',
          tenChiTiet: '–',
          cooperationTypeId: undefined,
          countKH: 0,
          // tenLienHe: '',
          // sdtLienHe: '',
          // loaiDon: '',
          doanhSoBanKH: null,
          doanhSoBanKHSum: 0,
          tienMatKH: null,
          tienMatKHSum: 0,
          chuyenKhoanKH: null,
          chuyenKhoanKHSum: 0,
          thuNoKH: null,
          thuNoKHSum: 0,
          thuTienKH: null,
          thuTienKHSum: 0,
          noHangHoaKH: null,
          noHangHoaKHSum: 0,
          noKhacKH: null,
          noKhacKHSum: 0,
          tongNoKH: null,
          tongNoKHSum: 0
        });
      }
    });

    setDataTongHop(sortAndIndex(tongHopRows));
    setDataKH(sortAndIndex(khRows));
  };

  const productList = useMemo(() => {
    // 1. Lọc từ category: Dùng hàm normalizeToCompare để đối chiếu với Set
    const base = (category || [])
      .filter((c: any) => c?.name && !EXCLUDED_PRODUCTS.has(normalizeToCompare(c.name)))
      .map((c: any) => ({ name: c.name, key: toSafeKey(c.name), index: c.index ?? 999 }));

    const known = new Set(base.map(p => p.name));
    (reportData || []).forEach(kd => {
      (kd.hangHoa || []).forEach(hh => {
        const name = hh?.tenSanPham || hh?.maSanPham;

        // 2. Lọc từ reportData: Vẫn kiểm tra tên gốc trong known, nhưng check ngoại lệ bằng chuỗi đã chuẩn hoá
        if (name && !known.has(name) && !EXCLUDED_PRODUCTS.has(normalizeToCompare(name))) {
          known.add(name);
          base.push({ name, key: toSafeKey(name), index: 999 });
        }
      });
    });

    return base.sort((a, b) => {
      if (a.index !== b.index) return a.index - b.index;
      return a.name.localeCompare(b.name, 'vi', { sensitivity: 'variant' });
    });
  }, [category, reportData]);

  // Dựng 2 bảng pivot: Doanh số theo Hàng hóa & Số lượng theo Hàng hóa, mỗi dòng = 1 nhân viên
  const { dataDoanhSoHH, dataSoLuongHH } = useMemo(() => {
    const dsRows: any[] = [];
    const slRows: any[] = [];

    (reportData || []).forEach(kd => {
      const dsRow: any = { saleAccount: kd.saleAccount, tongCong: 0 };
      const slRow: any = { saleAccount: kd.saleAccount, tongCong: 0 };

      // Show mặc định hết cột sản phẩm theo category, chưa phát sinh thì = 0
      productList.forEach(p => {
        dsRow[p.key] = 0;
        slRow[p.key] = 0;
      });

      // Merge dữ liệu thực tế của nhân viên KD vào đúng cột sản phẩm
      (kd.hangHoa || []).forEach(hh => {
        const name = hh.tenSanPham || hh.maSanPham;
        const key = toSafeKey(name);
        dsRow[key] = (dsRow[key] || 0) + (hh.tongTien || 0);
        slRow[key] = (slRow[key] || 0) + (hh.soLuong || 0);
        dsRow.tongCong += hh.tongTien || 0;
        slRow.tongCong += hh.soLuong || 0;
      });

      dsRows.push(dsRow);
      slRows.push(slRow);
    });

    return { dataDoanhSoHH: sortAndIndex(dsRows), dataSoLuongHH: sortAndIndex(slRows) };
  }, [reportData, productList]);
  // Xóa useMemo tạo dataDoanhSoHH và dataSoLuongHH cũ, thay bằng:
  // const { dataHangHoa } = useMemo(() => {
  //   const hhRows: any[] = [];

  //   (reportData || []).forEach(kd => {
  //     const row: any = { saleAccount: kd.saleAccount, tongCongDS: 0, tongCongSL: 0 };

  //     (kd.hangHoa || []).forEach(hh => {
  //       const name = hh.tenSanPham || hh.maSanPham;
  //       const key = toSafeKey(name);

  //       // Lưu song song Doanh số và Số lượng với key khác biệt
  //       row[`${key}_DS`] = (row[`${key}_DS`] || 0) + (hh.tongTien || 0);
  //       row[`${key}_SL`] = (row[`${key}_SL`] || 0) + (hh.soLuong || 0);

  //       row.tongCongDS += hh.tongTien || 0;
  //       row.tongCongSL += hh.soLuong || 0;
  //     });

  //     hhRows.push(row);
  //   });

  //   return { dataHangHoa: sortAndIndex(hhRows) };
  // }, [reportData]);

  const callBackEvent: ICallBackEvents = {
    callBackOrdersReport: (inputValue?: any, inputType?: any) => {
      const body: any = { ...query1 };

      if (inputValue !== undefined) {
        if (inputType === 'OrderId') body.maDonHang = inputValue;
        else if (inputType === 'CustomerId') body.maKhachHang = inputValue;
      }
      if (timeSelected !== undefined) {
        body.from = timeSelected.beginTime;
        body.to = timeSelected.endTime;
      }
      body.companyBranchId = selectedBranch !== undefined ? selectedBranch.id : 0;
      if (selectedArrayOrder && selectedArrayOrder?.length !== 0) body.orderTypeId = selectedArrayOrder?.data;
      if (orderCoopType !== undefined) body.loaiHinhHopTac = orderCoopType === '-1' ? 0 : orderCoopType;
      if (orderTime !== undefined) body.typeDate = orderTime;
      if (typeBranch1 !== undefined) body.typeBranch = typeBranch1;
      if (selectedArrayListSalesV3 !== undefined) {
        body.saleAccount = selectedArrayListSalesV3;
      } else {
        body.saleAccount = '';
      }
      if (paymentMethod !== undefined) body.hinhThucThanhToan = paymentMethod;

      setQuery1(body);
    }
  };

  const getTimeGetEligibleInDebtOrders = async () => {
    try {
      const res = await AdminLogApi.GetConfigWithKey('TimeGetEligibleInDebtOrders');
      if (!res.data.error) setTimeDebtOrders(res.data.data.data);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    getTimeGetEligibleInDebtOrders();
  }, []);

  const changeData = (result: any) => {
    // Console log để kiểm tra cấu trúc thật của API trả về
    // console.log('API Dữ liệu trả về:', result);

    // Sửa lại cách lấy data: Tùy thuộc vào backend của bạn trả về { data: [...] } hay { data: { kinhDoanh: [...] } }
    // Ưu tiên lấy result.data.kinhDoanh (nếu có), nếu không có thì lấy thẳng mảng result.data, hoặc result.data.data
    let kinhDoanh: IKinhDoanhReport[] = [];

    if (Array.isArray(result?.data)) {
      kinhDoanh = result.data;
    } else if (Array.isArray(result?.data?.data)) {
      kinhDoanh = result.data.data;
    } else if (Array.isArray(result?.data?.kinhDoanh)) {
      kinhDoanh = result.data.kinhDoanh;
    }

    // Sort array cho Excel
    const sortedKinhDoanh = [...kinhDoanh].sort((a, b) =>
      (a.saleAccount || '').localeCompare(b.saleAccount || '', 'vi', { sensitivity: 'variant', caseFirst: 'upper' })
    );

    setReportData(sortedKinhDoanh); // Lưu lại cho hàm xuất Excel
    onLoadDetailAllOrder(kinhDoanh); // Dựng data cho DevExtreme (3 bảng)

    if (result?.error) message.error(t('System.FetchError'));
    if (kinhDoanh.length === 0) message.info(t('System.NoDataResponse'));

    dispatch(updatePageLoadingApi(false));
  };

  const [isLoading, handleAdd]: any = useBaoCaoDoanhThuTheoKinhDoanh({
    onSuccess: changeData,
    onError: () => {
      message.error(t('System.FetchError'));
      dispatch(updatePageLoadingApi(false));
    }
  });

  const handleOk = (values: Partial<any>) => handleAdd(values);

  useEffect(() => {
    dispatch(updatePageLoadingApi(true));
    handleOk(query1);
  }, [query1]);

  const reloadPage = () => {
    dispatch(updatePageLoadingApi(true));
    handleOk(query1);
  };

  useEffect(() => {
    if (tabKeyActive) dispatch(updateResetDateTime(true));
  }, [tabKeyActive]);

  // --- Hàm Xuất Excel 3 Sheets ---
  const handleExportExcel = async () => {
    if (!reportData || reportData.length === 0) {
      message.warning('Không có dữ liệu để xuất!');
      return;
    }
    dispatch(updatePageLoadingApi(true));
    try {
      const workbook = new ExcelJS.Workbook();
      const moneyFormat = '#,##0';

      // Style dùng chung cho 2 dòng tiêu đề gộp (giống caption cha "Thu tiền" của table)
      const headerGroupStyle = (row: ExcelJS.Row) => {
        row.font = { bold: true };
        row.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      };

      // Sheet 1: Tổng hợp NV
      const sheet1 = workbook.addWorksheet('Tổng hợp NV');
      sheet1.columns = [
        { key: 'nv', width: 25 },
        { key: 'ds', width: 18, style: { numFmt: moneyFormat } },
        { key: 'tienmat', width: 15, style: { numFmt: moneyFormat } },
        { key: 'chuyenkhoan', width: 15, style: { numFmt: moneyFormat } },
        { key: 'thuno', width: 15, style: { numFmt: moneyFormat } },
        { key: 'thutien', width: 15, style: { numFmt: moneyFormat } },
        { key: 'nohh', width: 15, style: { numFmt: moneyFormat } },
        { key: 'nokhac', width: 15, style: { numFmt: moneyFormat } },
        { key: 'tongno', width: 15, style: { numFmt: moneyFormat } }
      ];
      // Dòng 1: tiêu đề gộp, dòng 2: tiêu đề con của nhóm "Thu tiền" (cột 3-6)
      sheet1.getRow(1).values = [
        'Nhân viên',
        'Doanh số bán hàng',
        'Thu tiền',
        '',
        '',
        '',
        'Nợ hàng hóa',
        'Nợ khác',
        'Tổng nợ'
      ];
      sheet1.getRow(2).values = ['', '', 'Tiền mặt', 'Chuyển khoản', 'Thu nợ', 'Tổng', '', '', ''];
      sheet1.mergeCells(1, 1, 2, 1);
      sheet1.mergeCells(1, 2, 2, 2);
      sheet1.mergeCells(1, 3, 1, 6);
      sheet1.mergeCells(1, 7, 2, 7);
      sheet1.mergeCells(1, 8, 2, 8);
      sheet1.mergeCells(1, 9, 2, 9);
      headerGroupStyle(sheet1.getRow(1));
      headerGroupStyle(sheet1.getRow(2));

      // Sheet 2: Khách hàng
      const sheet2 = workbook.addWorksheet('Chi tiết Khách hàng');
      sheet2.columns = [
        { key: 'nv', width: 20 },
        { key: 'loai', width: 18 },
        { key: 'makh', width: 15 },
        { key: 'tenkh', width: 35 },
        { key: 'dskh', width: 18, style: { numFmt: moneyFormat } },
        { key: 'tienmat', width: 15, style: { numFmt: moneyFormat } },
        { key: 'chuyenkhoan', width: 15, style: { numFmt: moneyFormat } },
        { key: 'thuno', width: 15, style: { numFmt: moneyFormat } },
        { key: 'thutien', width: 15, style: { numFmt: moneyFormat } },
        { key: 'nohh', width: 15, style: { numFmt: moneyFormat } },
        { key: 'nokhac', width: 15, style: { numFmt: moneyFormat } },
        { key: 'tongno', width: 15, style: { numFmt: moneyFormat } }
      ];
      // Dòng 1: tiêu đề gộp, dòng 2: tiêu đề con của nhóm "Thu tiền" (cột 6-9)
      sheet2.getRow(1).values = [
        'Nhân viên',
        'Loại hình hợp tác',
        'Mã khách hàng',
        'Tên khách hàng',
        'Doanh số bán hàng',
        'Thu tiền',
        '',
        '',
        '',
        'Nợ hàng hóa',
        'Nợ khác',
        'Tổng nợ'
      ];
      sheet2.getRow(2).values = ['', '', '', '', '', 'Tiền mặt', 'Chuyển khoản', 'Thu nợ', 'Tổng', '', '', ''];
      sheet2.mergeCells(1, 1, 2, 1);
      sheet2.mergeCells(1, 2, 2, 2);
      sheet2.mergeCells(1, 3, 2, 3);
      sheet2.mergeCells(1, 4, 2, 4);
      sheet2.mergeCells(1, 5, 2, 5);
      sheet2.mergeCells(1, 6, 1, 9);
      sheet2.mergeCells(1, 10, 2, 10);
      sheet2.mergeCells(1, 11, 2, 11);
      sheet2.mergeCells(1, 12, 2, 12);
      headerGroupStyle(sheet2.getRow(1));
      headerGroupStyle(sheet2.getRow(2));
      // Dòng gom (group header) nằm phía trên các dòng chi tiết -> summaryBelow = false
      // để nút +/- thu gọn của Excel hoạt động đúng như groupIndex của table
      (sheet2.properties as any).outlineProperties = { summaryBelow: false, summaryRight: false };

      const products = productList.map(p => ({ name: p.name }));

      const sheet3 = workbook.addWorksheet('Hàng hóa - Doanh số');
      const sheet4 = workbook.addWorksheet('Hàng hóa - Số lượng');

      const buildPivotColumns = () => [
        { header: 'Nhân viên', key: 'nv', width: 20 },
        ...products.map(p => ({ header: p.name, key: p.name, width: 18, style: { numFmt: moneyFormat } })),
        { header: 'Tổng cộng', key: 'tongCong', width: 16, style: { numFmt: moneyFormat } }
      ];
      sheet3.columns = buildPivotColumns();
      sheet4.columns = buildPivotColumns();
      sheet3.getRow(1).font = { bold: true };
      sheet4.getRow(1).font = { bold: true };
      // const sheet3 = workbook.addWorksheet('Chi tiết Hàng hóa');

      // const buildPivotColumns = () => {
      //   const cols: any[] = [{ header: 'Nhân viên', key: 'nv', width: 20 }];

      //   products.forEach(p => {
      //     cols.push({
      //       header: `${p.name} (DS)`,
      //       key: `${toSafeKey(p.name)}_DS`,
      //       width: 18,
      //       style: { numFmt: moneyFormat }
      //     });
      //     cols.push({ header: `${p.name} (SL)`, key: `${toSafeKey(p.name)}_SL`, width: 15 });
      //   });

      //   cols.push({ header: 'Tổng Doanh Số', key: 'tongCongDS', width: 18, style: { numFmt: moneyFormat } });
      //   cols.push({ header: 'Tổng Số Lượng', key: 'tongCongSL', width: 15 });
      //   return cols;
      // };

      // sheet3.columns = buildPivotColumns();
      // sheet3.getRow(1).font = { bold: true };

      reportData.forEach(kd => {
        const thuTien = kd.tienDaThu || 0; // (kd.tienMat || 0) + (kd.chuyenKhoan || 0) + (kd.thuNo || 0);
        const noKhac = (kd.tongNo || 0) - (kd.noHangHoa || 0);

        sheet1.addRow({
          nv: kd.saleAccount,
          ds: kd.doanhSoBan,
          tienmat: kd.tienMat,
          chuyenkhoan: kd.chuyenKhoan,
          thuno: kd.thuNo,
          thutien: thuTien,
          nohh: kd.noHangHoa,
          nokhac: noKhac,
          tongno: kd.tongNo
        });

        // Gom nhóm giống table: groupIndex 0 = Nhân viên, groupIndex 1 = Loại hình hợp tác
        const nhomKhachHang = kd.nhomKhachHang || [];
        if (nhomKhachHang.length > 0) {
          // Dòng gom cấp Nhân viên: hiển thị tổng tiền của NV (lấy trực tiếp từ kd, khớp sheet "Tổng hợp NV")
          const nvGroupRow = sheet2.addRow({
            nv: kd.saleAccount,
            loai: '',
            makh: '',
            tenkh: `(${nhomKhachHang.length} khách hàng)`,
            // tenlienhe: '',
            // sdtlienhe: '',
            // loaidon: '',
            dskh: kd.doanhSoBan,
            tienmat: kd.tienMat || 0,
            chuyenkhoan: kd.chuyenKhoan || 0,
            thuno: kd.thuNo || 0,
            thutien: thuTien,
            nohh: kd.noHangHoa || 0,
            nokhac: noKhac,
            tongno: kd.tongNo
          });
          nvGroupRow.font = { bold: true };
          nvGroupRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
          });

          // Gom theo Loại hình hợp tác trong từng Nhân viên
          const groupByCoopType = new Map<number | undefined, INhomKhachHangKinhDoanh[]>();
          nhomKhachHang.forEach(kh => {
            const key = kh.cooperationTypeId;
            if (!groupByCoopType.has(key)) groupByCoopType.set(key, []);
            groupByCoopType.get(key)!.push(kh);
          });

          groupByCoopType.forEach((list, cooperationTypeId) => {
            let dskhSum = 0;
            let tienMatSum = 0;
            let chuyenKhoanSum = 0;
            let thuNoSum = 0;
            let thuTienSum = 0;
            let noHangHoaSum = 0;
            let tongNoSum = 0;

            list.forEach(kh => {
              const noHangHoaKH = kh.noHangHoa ?? 0;
              const tongNoKH = kh.tongNo ?? kh.no ?? 0;
              dskhSum += kh.doanhSoBan || 0;
              tienMatSum += kh.tienMat || 0;
              chuyenKhoanSum += kh.chuyenKhoan || 0;
              thuNoSum += kh.thuNo || 0;
              thuTienSum += kh.tienDaThu || 0;
              noHangHoaSum += noHangHoaKH;
              tongNoSum += tongNoKH;
            });
            const noKhacSum = tongNoSum - noHangHoaSum;

            // Dòng gom cấp Loại hình hợp tác: hiển thị tổng tiền của nhóm
            const coopGroupRow = sheet2.addRow({
              nv: '',
              loai: coopType(cooperationTypeId),
              makh: '',
              tenkh: `(${list.length} khách hàng)`,
              // tenlienhe: '',
              // sdtlienhe: '',
              // loaidon: '',
              dskh: dskhSum,
              tienmat: tienMatSum,
              chuyenkhoan: chuyenKhoanSum,
              thuno: thuNoSum,
              thutien: thuTienSum,
              nohh: noHangHoaSum,
              nokhac: noKhacSum,
              tongno: tongNoSum
            });
            coopGroupRow.font = { bold: true, italic: true };
            coopGroupRow.outlineLevel = 1;
            coopGroupRow.eachCell(cell => {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
            });

            list.forEach(kh => {
              const noHangHoaKH = kh.noHangHoa ?? 0;
              const tongNoKH = kh.tongNo ?? kh.no ?? 0;
              const noKhacKH = tongNoKH - noHangHoaKH;
              const thuTienKH = kh.tienDaThu || 0;

              const khRow = sheet2.addRow({
                nv: kd.saleAccount,
                loai: coopType(kh.cooperationTypeId),
                makh: kh.maKhachHang,
                tenkh: kh.tenKhachHang,
                // tenlienhe: kh.tenLienHe || '',
                // sdtlienhe: kh.sdtLienHe || '',
                // loaidon: normalizeArrayText(kh.loaiDon),
                dskh: kh.doanhSoBan,
                tienmat: kh.tienMat || 0,
                chuyenkhoan: kh.chuyenKhoan || 0,
                thuno: kh.thuNo || 0,
                thutien: thuTienKH,
                nohh: noHangHoaKH,
                nokhac: noKhacKH,
                tongno: tongNoKH
              });
              khRow.outlineLevel = 2;
            });
          });
        }

        // const hhRow: any = { nv: kd.saleAccount, tongCongDS: 0, tongCongSL: 0 };

        // (kd.hangHoa || []).forEach(hh => {
        //   const name = hh.tenSanPham || hh.maSanPham;
        //   const key = toSafeKey(name);
        //   hhRow[`${key}_DS`] = (hhRow[`${key}_DS`] || 0) + (hh.tongTien || 0);
        //   hhRow[`${key}_SL`] = (hhRow[`${key}_SL`] || 0) + (hh.soLuong || 0);
        //   hhRow.tongCongDS += hh.tongTien || 0;
        //   hhRow.tongCongSL += hh.soLuong || 0;
        // });

        // sheet3.addRow(hhRow);
        const dsRow: any = { nv: kd.saleAccount, tongCong: 0 };
        const slRow: any = { nv: kd.saleAccount, tongCong: 0 };

        // Show mặc định hết cột sản phẩm theo category
        products.forEach(p => {
          dsRow[p.name] = 0;
          slRow[p.name] = 0;
        });

        (kd.hangHoa || []).forEach(hh => {
          const name = hh.tenSanPham || hh.maSanPham;
          dsRow[name] = (dsRow[name] || 0) + (hh.tongTien || 0);
          slRow[name] = (slRow[name] || 0) + (hh.soLuong || 0);
          dsRow.tongCong += hh.tongTien || 0;
          slRow.tongCong += hh.soLuong || 0;
        });
        sheet3.addRow(dsRow);
        sheet4.addRow(slRow);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `BaoCao_KinhDoanh_${new Date().getTime()}.xlsx`);
      message.success('Xuất báo cáo thành công!');
    } catch (e) {
      message.error('Có lỗi xảy ra khi xuất Excel');
    } finally {
      dispatch(updatePageLoadingApi(false));
    }
  };

  // ==============================================================================
  // CẤU HÌNH BẢNG (DEVEXTREME COLUMNS) — 3 bảng khớp 3 sheet Excel
  // ==============================================================================
  const formatCustomSum = useCallback((val: number) => Helpers.FormatMoney(val, true), []);

  // Ẩn số 0 khi hiển thị trong ô (nhưng data vẫn giữ giá trị 0 để tổng cộng/summary tính đúng)
  const renderPivotCell = useCallback((cell: any) => {
    const val = cell?.value;
    if (val === null || val === undefined || Number(val) === 0) return '';
    return Helpers.FormatMoney(Number(val), true);
  }, []);

  // Bảng 1: Tổng quan (Tổng hợp NV) — mỗi dòng là 1 nhân viên, giống Sheet "Tổng hợp NV"
  const columnsTongHop: IColumnTable[] = useMemo(
    () => [
      {
        dataField: 'saleAccount',
        caption: 'Nhân viên KD',
        width: 220,
        allowSorting: true,
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'saleAccount',
        summaryType: 'count',
        displayFormat: '{0} KD'
      },
      {
        dataField: 'doanhSoBan',
        caption: 'Doanh số bán hàng',
        width: 150, // Thêm width
        alignment: 'right',
        format: 'fixedPoint',
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'doanhSoBan',
        formatCustomSum
      },
      {
        caption: 'Thu tiền',
        childHeader: [
          {
            dataField: 'tienMat',
            caption: 'Tiền mặt',
            width: 130, // Thêm width
            alignment: 'right',
            format: 'fixedPoint',
            isSumFooter: true,
            isSumCol: true,
            sumCol: 'tienMat',
            formatCustomSum
          },
          {
            dataField: 'chuyenKhoan',
            caption: 'Chuyển khoản',
            width: 130, // Thêm width
            alignment: 'right',
            format: 'fixedPoint',
            isSumFooter: true,
            isSumCol: true,
            sumCol: 'chuyenKhoan',
            formatCustomSum
          },
          {
            dataField: 'thuNo',
            caption: 'Thu nợ',
            width: 130, // Thêm width
            alignment: 'right',
            format: 'fixedPoint',
            isSumFooter: true,
            isSumCol: true,
            sumCol: 'thuNo',
            formatCustomSum
          },
          {
            dataField: 'thuTien',
            caption: 'Tổng',
            width: 130, // Thêm width
            alignment: 'right',
            format: 'fixedPoint',
            isSumFooter: true,
            isSumCol: true,
            sumCol: 'thuTien',
            formatCustomSum
          }
        ]
      },
      {
        dataField: 'noHangHoa',
        caption: 'Nợ hàng hóa',
        width: 130, // Thêm width
        alignment: 'right',
        format: 'fixedPoint',
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'noHangHoa',
        formatCustomSum
      },
      {
        dataField: 'noKhac',
        caption: 'Nợ khác',
        width: 130, // Thêm width
        alignment: 'right',
        format: 'fixedPoint',
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'noKhac',
        formatCustomSum
      },
      {
        dataField: 'tongNo',
        caption: 'Tổng nợ',
        minWidth: 150, // Cột cuối dùng minWidth để tự co giãn lấp đầy bảng
        alignment: 'right',
        format: 'fixedPoint',
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'tongNo',
        formatCustomSum
      }
    ],
    [formatCustomSum]
  );

  const renderGroupHeaderKD = useCallback((cellInfo: any) => <span className="font-bold">{cellInfo.value}</span>, []);

  const renderGroupHeaderCoopType = useCallback(
    (cellInfo: any) => <span className="font-bold">{coopType(cellInfo.value)}</span>,
    []
  );

  const columnsKH: IColumnTable[] = useMemo(
    () => [
      {
        dataField: 'cooperationTypeId',
        caption: 'Loại hình hợp tác',
        groupIndex: 1, // 👈 đổi từ 0 → 1
        groupCellRender: renderGroupHeaderCoopType
      },
      {
        dataField: 'saleAccount',
        allowSorting: false,
        caption: 'Nhân viên KD',
        groupIndex: 0, // 👈 đổi từ 1 → 0
        groupCellRender: renderGroupHeaderKD
      },
      {
        type: 'buttons',
        dataField: 'Function',
        alignment: 'center',
        width: 150,
        allowSorting: false,
        allowFiltering: false,
        cellRender: (cellInfo: any) => {
          const row = cellInfo.data;
          return <ViewButton onClick={() => openCustomerDebtDetail(row)} title="Xem chi tiết" />;
        }
      },
      {
        dataField: 'maChiTiet',
        caption: 'Mã KH',
        width: 120,
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'maChiTiet'
        // summaryType: 'count'
        // displayFormat: '{0} mã'
      },
      {
        dataField: 'tenChiTiet',
        caption: 'Tên khách hàng',
        isSumFooter: true,
        isSumCol: true,
        showInGroupFooter: false,
        sumCol: 'countKH',
        displayFormat: '{0} khách hàng',
        width: 220
      },
      {
        dataField: 'doanhSoBanKH',
        caption: 'Doanh số bán hàng',
        width: 150, // Thêm width
        alignment: 'right',
        format: 'fixedPoint',
        sumCol: 'doanhSoBanKHSum',
        isSumFooter: true,
        isSumCol: true,
        formatCustomSum
      },
      {
        caption: 'Thu tiền',
        childHeader: [
          {
            dataField: 'tienMatKH',
            caption: 'Tiền mặt',
            width: 130, // Thêm width
            alignment: 'right',
            format: 'fixedPoint',
            sumCol: 'tienMatKHSum',
            isSumFooter: true,
            isSumCol: true,
            formatCustomSum
          },
          {
            dataField: 'chuyenKhoanKH',
            caption: 'Chuyển khoản',
            width: 130, // Thêm width
            alignment: 'right',
            format: 'fixedPoint',
            sumCol: 'chuyenKhoanKHSum',
            isSumFooter: true,
            isSumCol: true,
            formatCustomSum
          },
          {
            dataField: 'thuNoKH',
            caption: 'Thu nợ',
            width: 130, // Thêm width
            alignment: 'right',
            format: 'fixedPoint',
            sumCol: 'thuNoKHSum',
            isSumFooter: true,
            isSumCol: true,
            formatCustomSum
          },
          {
            dataField: 'thuTienKH',
            caption: 'Tổng',
            width: 130, // Thêm width
            alignment: 'right',
            format: 'fixedPoint',
            sumCol: 'thuTienKHSum',
            isSumFooter: true,
            isSumCol: true,
            formatCustomSum
          }
        ]
      },
      {
        dataField: 'noHangHoaKH',
        caption: 'Nợ hàng hóa',
        width: 130, // Thêm width
        alignment: 'right',
        format: 'fixedPoint',
        sumCol: 'noHangHoaKHSum',
        isSumFooter: true,
        isSumCol: true,
        formatCustomSum
      },
      {
        dataField: 'noKhacKH',
        caption: 'Nợ khác',
        width: 130, // Thêm width
        alignment: 'right',
        format: 'fixedPoint',
        sumCol: 'noKhacKHSum',
        isSumFooter: true,
        isSumCol: true,
        formatCustomSum
      },
      {
        dataField: 'tongNoKH',
        caption: 'Tổng nợ',
        minWidth: 150, // Cột cuối cùng dùng minWidth
        alignment: 'right',
        format: 'fixedPoint',
        sumCol: 'tongNoKHSum',
        isSumFooter: true,
        isSumCol: true,
        formatCustomSum
      }
    ],
    [formatCustomSum, renderGroupHeaderCoopType, renderGroupHeaderKD]
  );

  const columnsDoanhSoHH: IColumnTable[] = useMemo(
    () => [
      {
        dataField: 'saleAccount',
        caption: 'Nhân viên KD',
        width: 180,
        allowSorting: true,
        fixed: true,
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'saleAccount',
        summaryType: 'count',
        displayFormat: '{0} KD'
      },
      ...productList.map(p => ({
        dataField: p.key,
        caption: `${t(p.name)}`,
        alignment: 'right' as const,
        format: 'fixedPoint',
        isSumFooter: true,
        isSumCol: true,
        sumCol: p.key,
        width: p.name === 'CPU' ? 80 : 120,
        formatCustomSum,
        cellRender: renderPivotCell // 👈 thêm dòng này
      })),
      {
        dataField: 'tongCong',
        caption: 'Tổng cộng',
        alignment: 'right',
        format: 'fixedPoint',
        width: 100,
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'tongCong',
        formatCustomSum
      }
    ],
    [productList, formatCustomSum]
  );

  const columnsSoLuongHH: IColumnTable[] = useMemo(
    () => [
      {
        dataField: 'saleAccount',
        caption: 'Nhân viên KD',
        width: 180,
        allowSorting: true,
        fixed: true,
        isSumFooter: true,
        isSumCol: true,
        sumCol: 'saleAccount',
        summaryType: 'count',
        displayFormat: '{0} KD'
      },
      ...productList.map(p => ({
        dataField: p.key,
        caption: `${t(p.name)}`,
        alignment: 'right' as const,
        isSumFooter: true,
        isSumCol: true,
        sumCol: p.key,
        width: p.name === 'CPU' ? 80 : 120,
        cellRender: (cell: any) => {
          const val = cell?.value;
          if (val === null || val === undefined || Number(val) === 0) return '';
          return val;
        } // 👈 số lượng không format tiền, chỉ ẩn 0
      })),
      {
        dataField: 'tongCong',
        caption: 'Tổng cộng',
        alignment: 'right',
        isSumFooter: true,
        isSumCol: true,
        width: 100,
        sumCol: 'tongCong'
        // cellRender: (cell: any) => {
        //   const val = cell?.value;
        //   if (val === null || val === undefined || Number(val) === 0) return '';
        //   return val;
        // }
      }
    ],
    [productList]
  );

  // Xóa columnsDoanhSoHH và columnsSoLuongHH cũ, thay bằng:
  // const columnsHangHoa: IColumnTable[] = useMemo(
  //   () => [
  //     { dataField: 'saleAccount', caption: 'Nhân viên KD', width: 180, allowSorting: true, fixed: true },

  //     // Gom 1 cột cho từng mã sản phẩm (Render bằng custom HTML)
  //     ...productList.map(p => ({
  //       caption: (p.name),
  //       alignment: 'right' as const,

  //       allowSorting: false, // Vì cột chứa HTML nên tắt sort
  //       cellRender: (cellInfo: any) => {
  //         const rowData = cellInfo.data;
  //         const ds = rowData[`${p.key}_DS`] || 0;
  //         const sl = rowData[`${p.key}_SL`] || 0;

  //         // Nếu không có cả DS và SL thì hiển thị dấu gạch ngang cho đỡ rối mắt
  //         if (ds === 0 && sl === 0) return <span className="text-gray-300">-</span>;

  //         return (
  //           <div className="flex flex-col text-right justify-center font-semibold">
  //             {/* Hiển thị Doanh số màu xanh cho nổi bật */}
  //             <span className=" ">{formatCustomSum(ds)}</span>
  //             {/* Hiển thị Số lượng màu xám nhạt nhỏ hơn bên dưới */}
  //             <span className="text-gray-500 text-xs mt-[2px]">Số lượng: {sl}</span>
  //           </div>
  //         );
  //       }
  //     })),

  //     // Giữ nguyên 2 cột Tổng cộng bằng config chuẩn của DevExtreme để Footer hoạt động
  //     {
  //       dataField: 'tongCongDS',
  //       caption: 'Tổng Doanh số',
  //       alignment: 'right',
  //       format: 'fixedPoint',
  //       isSumFooter: true,
  //       isSumCol: true,
  //       sumCol: 'tongCongDS',
  //       formatCustomSum,

  //       fixed: true, // Ghim cột này
  //       fixedPosition: 'right' // Ghim vào bên phải màn hình
  //     },
  //     {
  //       dataField: 'tongCongSL',
  //       caption: 'Tổng Số lượng',
  //       alignment: 'right',
  //       format: 'fixedPoint',
  //       isSumFooter: true,
  //       isSumCol: true,
  //       sumCol: 'tongCongSL',

  //       fixed: true, // Ghim cột này
  //       fixedPosition: 'right' // Ghim vào bên phải màn hình
  //     }
  //   ],
  //   [productList, formatCustomSum, ]
  // );

  const CustomToolbar = () => (
    <div className="flex items-center gap-2">
      <TableTitle title={t('Menu.ReportStatistic.ReportBySellStaffV2')} />
    </div>
  );

  const renderTongHopTable = useMemo(
    () => (
      <Table
        tableName={t('Report.ReportBySellStaff.TongQuan')}
        tableId="baocaotheonvkt_tonghop_v2"
        keyExpr="Id"
        oncClickReload={reloadPage}
        reload={true}
        columns={columnsTongHop}
        data={dataTongHop}
        fileNameExport={t('Report.ReportBySellStaff.TongQuan')}
        isStriped={false}
        hiddenAddButton={true}
        downHeight={12.8}
        wrapTextData={false}
        allowExcel={false}
        minPageSize={MINPAGESIZE3}
        customCenterToolbar={() => <CustomToolbar />}
      />
    ),
    [columnsTongHop, dataTongHop]
  );

  const renderKHTable = useMemo(
    () => (
      <Table
        tableName={t('Report.BaoCaoDoanhThuTheoKinhDoanh.KhachHang')}
        tableId="baocaotheonvkt_kh_v2"
        keyExpr="Id"
        reload={true}
        columns={columnsKH}
        data={dataKH}
        fileNameExport={t('Report.BaoCaoDoanhThuTheoKinhDoanh.KhachHang')}
        isStriped={false}
        hiddenAddButton={true}
        downHeight={12.8}
        wrapTextData={false}
        wrapText={true}
        allowExcel={false}
        minPageSize={MINPAGESIZE3}
        grouping={false}
        onClickRow={(e: any) => {
          if (e.rowType === 'data' && (e.column?.dataField === 'tenChiTiet' || e.column?.dataField === 'maChiTiet')) {
            openCustomerDebtDetail(e.data);
          }
        }}
        customCenterToolbar={() => <CustomToolbar />}
      />
    ),
    [columnsKH, dataKH, timeDebtOrders]
  );

  // const renderHangHoaTable = useMemo(() => {
  //   if (productList.length === 0) {
  //     return (
  //       <div className="py-16 flex flex-col items-center gap-2">
  //         <CustomToolbar />
  //         <Empty description="Không có dữ liệu hàng hóa trong kỳ đã chọn" />
  //       </div>
  //     );
  //   }
  //   return (
  //     <Table
  //       tableName="Hàng hóa"
  //       tableId="baocaotheonvkt_hh_v2"
  //       keyExpr="Id"
  //       columns={columnsHangHoa}
  //       data={dataHangHoa}
  //       wrapText={true}
  //       fileNameExport="HangHoa_ChiTiet"
  //       isStriped={false}
  //       hiddenAddButton={true}
  //       downHeight={12.8}
  //       wrapTextData={false}
  //       minPageSize={MINPAGESIZE3}
  //       allowExcel={false}
  //       // columnAutoWidth={false}
  //       customCenterToolbar={() => <CustomToolbar />}
  //     />
  //   );
  // }, [columnsHangHoa, dataHangHoa, productList]);

  const renderDoanhSoHHTable = useMemo(() => {
    if (productList.length === 0) {
      return (
        <div className="py-16 flex flex-col items-center gap-2">
          <CustomToolbar />
          <Empty description="Không có dữ liệu hàng hóa trong kỳ đã chọn" />
        </div>
      );
    }
    return (
      <Table
        tableName="Hàng hóa - Doanh số"
        tableId="baocaotheonvkt_hhds_v2"
        keyExpr="Id"
        columns={columnsDoanhSoHH}
        data={dataDoanhSoHH}
        wrapText={true}
        fileNameExport="HangHoa_DoanhSo"
        isStriped={false}
        hiddenAddButton={true}
        downHeight={12.8}
        wrapTextData={false}
        minPageSize={MINPAGESIZE3}
        allowExcel={false}
        columnAutoWidth={false}
        customCenterToolbar={() => <CustomToolbar />}
      />
    );
  }, [columnsDoanhSoHH, dataDoanhSoHH, productList]);

  const renderSoLuongHHTable = useMemo(() => {
    if (productList.length === 0) {
      return (
        <div className="py-16 flex flex-col items-center gap-2">
          <CustomToolbar />
          <Empty description="Không có dữ liệu hàng hóa trong kỳ đã chọn" />
        </div>
      );
    }
    return (
      <Table
        tableName="Hàng hóa - Số lượng"
        tableId="baocaotheonvkt_hhsl_v2"
        keyExpr="Id"
        columns={columnsSoLuongHH}
        data={dataSoLuongHH}
        wrapText={true}
        fileNameExport="HangHoa_SoLuong"
        isStriped={false}
        hiddenAddButton={true}
        downHeight={12.8}
        wrapTextData={false}
        minPageSize={MINPAGESIZE3}
        allowExcel={false}
        columnAutoWidth={false}
        customCenterToolbar={() => <CustomToolbar />}
      />
    );
  }, [columnsSoLuongHH, dataSoLuongHH, productList]);

  return (
    <Layout path={[{ name: 'Báo cáo doanh thu theo kinh doanh' }]}>
      {selectedCustomer && (
        <CustomerDebtDetailModal
          visible={debtModalVisible}
          onClose={() => setDebtModalVisible(false)}
          maKhachHang={selectedCustomer.maKhachHang}
          saleAccount={selectedCustomer.saleAccount}
          from={query1.from}
          to={query1.to}
        />
      )}
      <div className="relative pt-2 -top-[10px]">
        <div className="flex flex-wrap items-end justify-between w-full px-2 gap-2 mb-2">
          {/* Bọc SelectPanel vào 1 div flex-1 để nó chiếm phần không gian còn lại */}
          <div className="flex-1">
            <SelectPanel
              type={[EModePanelSelect.ListSalesV3]}
              checkType="tongquanKD"
              allowHTTT={true}
              isHiddenDefaultSelect={true}
              isMultipleSelect={false}
              isAllOption={true}
              checkSelect={false}
              callBackEvents={callBackEvent}
              // isMutipleOrder={[EModePanelSelect.Order]}
            />
          </div>

          {/* Nút Export sẽ bị đẩy về bên phải và gióng đáy (items-end) với form bộ lọc */}
          <Button
            type="primary"
            icon={<DownloadOutlined className="-translate-y-[3px]" />}
            onClick={handleExportExcel}
            className="bg-green-600 hover:bg-green-500 border-none shadow mb-1"
          >
            Xuất Báo Cáo
          </Button>
        </div>

        <Tabs
          type="card"
          destroyInactiveTabPane
          className=""
          activeKey={activeTab}
          // onChange={key => setActiveTab(key as 'tonghop' | 'kh' | 'hhds' | 'hhsl')}
          onChange={key => setActiveTab(key as 'tonghop' | 'kh' | 'hh')}
        >
          <Tabs.TabPane tab={t('Report.ReportBySellStaff.TitleTongQuan')} key="tonghop">
            {renderTongHopTable}
          </Tabs.TabPane>
          <Tabs.TabPane tab={t('Report.BaoCaoDoanhThuTheoKinhDoanh.KhachHang')} key="kh">
            {renderKHTable}
          </Tabs.TabPane>
          <Tabs.TabPane tab="Hàng hóa - Doanh số" key="hhds">
            {renderDoanhSoHHTable}
          </Tabs.TabPane>
          <Tabs.TabPane tab="Hàng hóa - Số lượng" key="hhsl">
            {renderSoLuongHHTable}
          </Tabs.TabPane>
          {/* <Tabs.TabPane tab="Chi tiết Hàng hóa" key="hh">
            {renderHangHoaTable}
          </Tabs.TabPane> */}
        </Tabs>
      </div>
    </Layout>
  );
}
